import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { Action } from "../shared/types";

type Routing = NonNullable<Action["routing"]>;

function targetLabel(routing: Routing, t: ReturnType<typeof useTranslation>["t"]): string {
  if (routing.mode === "team") return t("build.actions.card.routing.team", { name: routing.target_name ?? "" });
  if (routing.mode === "person") return t("build.actions.card.routing.person", { name: routing.target_name ?? "" });
  return t("build.actions.card.routing.fallback");
}

/**
 * Talk to a person: where handoffs go. A read-only view of Agent Settings > Escalation & handoff
 * (the single place the routing is stored); the link opens that tab. Sits above the card's
 * click-to-open overlay (`z-10`) so it stays a real link.
 */
export function RoutingLine({ routing, agentId }: { routing: Routing; agentId: string }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const label = targetLabel(routing, t);
  return (
    <div className="relative z-10 mt-3 space-y-1 text-xs" data-testid="handoff-routing">
      <div className="flex flex-wrap items-center gap-x-1.5 text-fg-muted">
        <Icon name="users" className="size-3.5" />
        <span>{t("build.actions.card.routing.label")}</span>
        <AppLink
          href={consoleRoutes.agentSettings(summary.slug, agentId, "escalation")}
          aria-label={t("build.actions.card.routing.change", { target: label })}
          className="font-semibold text-primary-ink underline-offset-2 hover:underline"
        >
          {label}
        </AppLink>
      </div>
      {routing.payment_team_name ? (
        <div className="text-fg-subtle">{t("build.actions.card.routing.payment", { name: routing.payment_team_name })}</div>
      ) : null}
    </div>
  );
}
